import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import dynamic from 'next/dynamic';
import IconFont from '$/components/IconFont';
import type { MyIpRoleCardGenerationResult } from '$/domains/myIp/roleCardGeneration';
import { trackMyIpRoleCardSave } from '$/domains/myIp/tracking';
import { useMyIpCopy } from '$/pages/RoboneoMyIps/useMyIpCopy';
import { downloadFileFromUrl } from '$/utils/download';
import { handleImageUrl } from '$/utils/image';
import {
  getAigcWaterMarkSwitch,
  resolveNoBrandWatermarkDownloadUrlByApi,
} from '$/utils/noBrandWatermarkDownload';
import styles from './roleCard.module.scss';

const Lanyard = dynamic(() => import('./Lanyard'), {
  ssr: false,
  loading: () => <div className={styles.lanyardLoading} aria-hidden="true" />,
});

export interface MyIpRoleCardModalProps {
  open: boolean;
  ipId: string;
  /** 当前 IP 的全部角色卡结果，按生成时间倒序展示。 */
  results: MyIpRoleCardGenerationResult[];
  /** 当前 IP 的主视图，作为 3D 角色卡背面纹理。 */
  backImage?: string;
  /** 国内展示品牌水印，海外展示无品牌水印原图。 */
  useWatermark?: boolean;
  /** 首次打开时正在读取当前 IP 的角色卡历史。 */
  loading?: boolean;
  onClose: () => void;
}

/** 角色卡与详情素材共用地区水印规则，海外优先使用转存原图。 */
function getRoleCardDisplayUrl(result: MyIpRoleCardGenerationResult, useWatermark: boolean): string {
  return useWatermark ? result.url : result.originalUrl?.trim() || result.url;
}

/** 角色卡历史弹窗对齐 PRD：用 3D 挂绳展示选中结果，并支持切换、保存与关闭。 */
export default function MyIpRoleCardModal({
  open, ipId, results, backImage, useWatermark = true, loading = false, onClose,
}: MyIpRoleCardModalProps) {
  const { roleCard: copy } = useMyIpCopy();
  const [saving, setSaving] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const selectedResult = results[selectedIndex] ?? results[0];
  const displayUrl = selectedResult ? getRoleCardDisplayUrl(selectedResult, useWatermark) : '';

  useEffect(() => {
    setSelectedIndex(0);
    setSaveFailed(false);
  }, [open, results]);

  /** 选择历史中的另一张角色卡，下载按钮随当前选中结果切换。 */
  const handleSelect = (index: number) => {
    if (saving) return;
    setSelectedIndex(index);
    setSaveFailed(false);
  };

  /** 国内按品牌水印规则解析下载地址；海外直接保存转存原图。 */
  const handleSave = async () => {
    const brandWatermarkUrl = selectedResult?.url;
    const originalUrl = selectedResult?.originalUrl?.trim() || brandWatermarkUrl;
    const fileName = selectedResult?.fileName;
    if (!originalUrl || saving) return;
    setSaving(true);
    setSaveFailed(false);
    trackMyIpRoleCardSave('canvas_save_click', ipId);
    try {
      // 海外没有品牌水印，避免将水印地址交给下载解析接口。
      let downloadUrl = originalUrl;
      if (useWatermark) {
        const watermarkUrl = brandWatermarkUrl !== originalUrl
          ? brandWatermarkUrl
          : handleImageUrl(originalUrl, { showWatermark: true, needFormat: false });
        downloadUrl = await resolveNoBrandWatermarkDownloadUrlByApi({
          watermarkUrl,
          aigcWaterMarkSwitch: getAigcWaterMarkSwitch(),
          fallbackUrl: watermarkUrl,
          fallbackOnError: false,
          preferApiUrl: true,
        });
      }
      await downloadFileFromUrl(downloadUrl, fileName, { preferSuggestedFilename: true });
      trackMyIpRoleCardSave('canvas_save_success', ipId);
    } catch {
      trackMyIpRoleCardSave('canvas_save_fail', ipId);
      setSaveFailed(true);
    } finally {
      setSaving(false);
    }
  };

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <div className={styles.overlay} role="presentation">
      <button type="button" className={styles.backdrop} aria-label={copy.close} onClick={onClose} />
      <div className={styles.dialog} role="dialog" aria-modal="true" aria-label={copy.title}>
        {!loading && !selectedResult && (
          <div className={styles.empty} role="status">
            <IconFont name="drawerBold" fontSize={40} className={styles.emptyIcon} />
            <p className={styles.emptyText}>{copy.empty}</p>
          </div>
        )}
        {loading && <p className={styles.emptyText} role="status">{copy.loading}</p>}
        {!loading && selectedResult && (
          <>
            <Lanyard
              // 切换角色卡时重置 3D 物理姿态，避免上一张卡旋转到侧面后被新卡继承。
              key={selectedResult.taskId || selectedResult.url}
              className={styles.lanyardCanvas}
              position={[0, 0, 14]}
              gravity={[0, -40, 0]}
              frontImage={displayUrl}
              backImage={backImage}
              lanyardWidth={0.72}
              // 挂绳视觉上移 6px，与下方金属扣留出更舒适的间距。
              lanyardOffsetY={6}
            />
            {results.length > 1 && (
              <div className={styles.history} role="list" aria-label={copy.title}>
                {results.map((result, index) => (
                  <button
                    type="button"
                    key={result.taskId}
                    className={styles.historyItem}
                    aria-label={`${copy.title} ${index + 1}`}
                    aria-pressed={selectedIndex === index}
                    disabled={saving}
                    onClick={() => handleSelect(index)}
                  >
                    <img src={getRoleCardDisplayUrl(result, useWatermark)} alt="" crossOrigin="anonymous" />
                  </button>
                ))}
              </div>
            )}
          </>
        )}
        <div className={styles.actions}>
          <button type="button" className={styles.save} onClick={handleSave} disabled={saving || !selectedResult} aria-busy={saving}>
            <IconFont name="downloadBold" fontSize={16} />
            {copy.saveToLocal}
          </button>
          <button type="button" className={styles.close} onClick={onClose} aria-label={copy.close}>
            <IconFont name="crossBold" fontSize={16} />
          </button>
        </div>
        {saveFailed && <p className={styles.saveError} role="alert">{copy.saveError}</p>}
      </div>
    </div>,
    document.body,
  );
}
